/**
 * 음성 조각 저장 사용량 — 관리 콘솔 (2026-09-21).
 *
 * ## ★★★ 이것이 없으면 한도는 쓸모가 없다
 *
 * 「연결 대기」 동안 서버는 음성 조각(WAV)을 **안 지운다** — 엔진 꽂는 날 전사할 원본이 필요하고
 * 보관본(MP3 48k)은 손실이라 못 쓴다. 그런데 조각은 `voice_chunks` 에 살아 **개인 5GiB 쿼터에
 * 안 잡힌다.** 즉 **여기가 유일하게 보이는 자리**다.
 *
 * ## 서버가 «안 만드는» 것 — 콘솔이 만든다
 *
 * 서버는 **바이트 숫자만** 준다. 「12.0 GiB」 같은 사람이 읽는 값도, 퍼센트도 서버가 안 만든다 —
 * 만들면 **단위·반올림 규칙이 두 벌**이 되고 언젠가 어긋난다. 그 몫은 `voice-storage-view.ts` 에 있다.
 *
 * ## 404 는 «오류»가 아니라 «상태»다
 *
 * ✅ 라우트는 **섰다**(2026-09-24 확인 — `modules/voice/voice-admin.routes.ts:119` ·
 * `lib/server-routes.ts:693` 에 등록). 그래도 이 갈래를 **남겨 둔다**:
 * 배포가 **chat-server 먼저 · 콘솔 나중**이라 그 사이 창이 늘 생기고, 되돌린 판에서도 404 가 온다.
 * **404 를 빨간 오류로 그리면 운영자가 고장으로 읽는다.**
 * ⇒ 404 만 `{ ready: false }` 로 갈라서 돌려준다 — 서면 화면이 **저절로** 열린다.
 */
import { AxiosError } from 'axios';
import { apiClient } from './client';

/** 서버가 주는 칸 — ★ 업로드 413 응답과 **같은 글자**다. 한 뜻에 이름 하나. */
export interface VoiceStorageUsage {
  /** ⚠ 숫자다(문자열 아님). 50GiB 는 2^53 아래라 안전하다. */
  used_bytes: number;
  limit_bytes: number;
  session_count: number;
  chunk_count: number;
  /** ★ 한도가 찼을 때 「무엇을 지울지」 고를 **유일한 실마리**. 없으면 null. */
  oldest_chunk_at: string | null;
}

/** 전 회사를 한 번에 — 회사마다 같은 칸을 묶어서 준다. */
export interface VoiceStorageRow extends VoiceStorageUsage {
  institution_id: string;
  code: string | null;
  name: string;
}

export type VoiceStorageResult =
  | { ready: true; institutions: VoiceStorageRow[] }
  /** 그 판이 아직 안 올라온 서버다(404) — 고장이 아니다. */
  | { ready: false };

function isNotServedYet(e: unknown): boolean {
  const status = (e as AxiosError | undefined)?.response?.status;
  // ⚠ 404 만 본다 — 500·502 는 «진짜 탈»이라 오류로 보여야 한다(둘을 뭉치면 고장을 숨긴다).
  return status === 404;
}

/**
 * 전 회사 사용량. 콘솔은 여러 회사를 보는 화면이라 **회사마다 따로 부르지 않는다**
 * — 사람당 분당 버킷에 걸린다(날씨 위치에서 겪은 그것).
 */
export async function listVoiceStorage(): Promise<VoiceStorageResult> {
  try {
    const { data } = await apiClient.get<{ institutions: VoiceStorageRow[] }>('/admin/voice/storage/all');
    return { ready: true, institutions: data.institutions ?? [] };
  } catch (e: unknown) {
    if (isNotServedYet(e)) return { ready: false };
    throw e;
  }
}
